<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-card" role="dialog" aria-modal="true">
      <header class="modal-header">
        <h2>Add Market — pick a tenant</h2>
        <button type="button" class="close-x" @click="close" aria-label="Close">×</button>
      </header>
      <div class="modal-body">
        <p class="muted small">
          After you pick a tenant, you'll draw their market on the map — same
          experience as the agent signup flow.
        </p>
        <input
            v-model="tenant_query"
            type="text"
            class="search-input"
            placeholder="Filter tenants without a market — name, email, phone…"
        />
        <div class="results">
          <div v-if="searching" class="muted small pad">Loading tenants…</div>
          <div v-else-if="!filtered_results.length" class="muted small pad">
            {{ tenants_loaded ? 'No tenants without a market match that filter.' : 'No tenants loaded yet.' }}
          </div>
          <button
              v-for="r in filtered_results"
              :key="r.tenant_id"
              type="button"
              class="result-row"
              :class="{ selected: selected_tenant && selected_tenant.tenant_id === r.tenant_id }"
              @click="selected_tenant = r"
          >
            <span class="r-name">{{ r.name || '—' }}</span>
            <span class="r-email">{{ r.email || '' }}</span>
            <span class="r-phone">{{ r.phone || '' }}</span>
          </button>
        </div>
        <p class="muted small mt-1">
          {{ all_results.length }} tenant{{ all_results.length === 1 ? '' : 's' }} eligible.
        </p>
        <div v-if="error" class="alert">{{ error }}</div>
      </div>
      <footer class="modal-footer">
        <button type="button" class="btn ghost" @click="close">Cancel</button>
        <button
            type="button"
            class="btn primary"
            :disabled="!selected_tenant"
            @click="next"
        >Draw market →</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { searchUsers, humanizeAxiosError } from './markets_api';

export default {
  name: 'AddMarketModal',
  props: {
    show: { type: Boolean, required: true },
  },
  data() {
    return {
      tenant_query: '',
      all_results: [],
      tenants_loaded: false,
      searching: false,
      selected_tenant: null,
      error: '',
    };
  },
  computed: {
    filtered_results() {
      const q = (this.tenant_query || '').trim().toLowerCase();
      if (!q) return this.all_results;
      return this.all_results.filter((r) => {
        const hay = [r.name, r.email, r.phone, r.company_name, r.owner_name]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    },
  },
  watch: {
    show(v) {
      if (v && !this.tenants_loaded) this.loadAll();
    },
  },
  methods: {
    close() { this.$emit('update:show', false); },
    next() {
      if (!this.selected_tenant) return;
      this.$emit('select-tenant', this.selected_tenant);
    },
    async loadAll() {
      this.searching = true;
      try {
        this.all_results = await searchUsers('');
        this.tenants_loaded = true;
      } catch (e) {
        this.error = humanizeAxiosError(e) || 'Failed to load tenants';
      } finally {
        this.searching = false;
      }
    },
  },
  mounted() {
    if (this.show) this.loadAll();
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; z-index: 10010;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
}
.modal-card {
  background: #fff; border-radius: 12px;
  width: 720px; max-width: 100%;
  max-height: calc(100vh - 64px); overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #eef1f5;
}
.modal-header h2 { margin: 0; font-size: 16px; font-weight: 700; color: #061D47; }
.close-x {
  background: transparent; border: 0; cursor: pointer; font-size: 24px;
  color: #6b7280; line-height: 1; padding: 0 4px;
}
.close-x:hover { color: #1f2937; }
.modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid #eef1f5; background: #fafbfd;
}
.search-input {
  width: 100%; box-sizing: border-box;
  padding: 8px 12px; border: 1px solid #c2cbe0; border-radius: 6px;
  font-size: 14px;
}
.search-input:focus { outline: none; border-color: #061D47; box-shadow: 0 0 0 2px rgba(6,29,71,0.12); }
.results {
  margin-top: 12px;
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid #eef1f5; border-radius: 6px;
  background: #fff;
}
.result-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px; align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: 0; border-bottom: 1px solid #f4f6f9;
  cursor: pointer; text-align: left;
  font-size: 13px; color: #1f2937;
}
.result-row:last-child { border-bottom: 0; }
.result-row:hover { background: #f7f9fc; }
.result-row.selected { background: #eaf2ff; }
.result-row .r-name { font-weight: 600; }
.result-row .r-email, .result-row .r-phone { color: #6b7280; }
.muted { color: #6b7280; }
.small { font-size: 13px; }
.pad { padding: 12px; }
.mt-1 { margin-top: 6px; }
.alert {
  margin-top: 12px;
  padding: 8px 12px; background: #fff5f5;
  border: 1px solid #fbd5d5; border-radius: 6px;
  color: #7E0F0F; font-size: 13px;
}
.btn {
  border: 0; border-radius: 6px;
  padding: 8px 14px; font-size: 14px; font-weight: 600;
  cursor: pointer;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn.ghost { background: transparent; color: #4b5563; }
.btn.ghost:hover:not(:disabled) { background: #eef1f5; }
.btn.primary { background: #061D47; color: #fff; }
.btn.primary:hover:not(:disabled) { background: #0b2a66; }
</style>
